import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

/**
 * Iter208.66 — Restyling Google Calendar del picker slot sopralluogo.
 *
 * Look & feel:
 *  - Sfondo bianco, celle rotonde, tipografia leggera.
 *  - Oggi = cerchio outline viola (senza fill).
 *  - Giorno selezionato = cerchio pieno viola.
 *  - Giorno con slot = pallino verde sotto il numero (non viola).
 *  - Giorni passati o senza slot = grigio chiaro, non cliccabili.
 *  - Header mese: "Settembre 2026" con frecce navigazione.
 *  - Griglia L M M G V S D in font leggero e capital.
 *
 *  A destra: slot orari come CHIP quadrate (grid 3 colonne, no lista).
 */
function toISODate(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
const TODAY_ISO = (() => {
  const t = new Date();
  return toISODate(t.getFullYear(), t.getMonth() + 1, t.getDate());
})();

export default function SlotPicker({
  slotMonth,
  monthGrid,
  selectedDate,
  slotsForSelectedDate,
  chosenSlot,
  totalSlotsCount,
  onSetSlotMonth,
  onSetSelectedDate,
  onSetChosenSlot,
}) {
  const prevMonth = () =>
    onSetSlotMonth({
      year: slotMonth.month === 1 ? slotMonth.year - 1 : slotMonth.year,
      month: slotMonth.month === 1 ? 12 : slotMonth.month - 1,
    });
  const nextMonth = () =>
    onSetSlotMonth({
      year: slotMonth.month === 12 ? slotMonth.year + 1 : slotMonth.year,
      month: slotMonth.month === 12 ? 1 : slotMonth.month + 1,
    });

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm"
      data-testid="slot-picker-calendly"
    >
      {/* Header mese pulito */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            Sopralluogo
          </div>
          <div className="text-lg font-display font-semibold text-slate-900 leading-tight mt-0.5">
            {MONTHS[slotMonth.month - 1]} {slotMonth.year}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={prevMonth}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 transition-colors"
            data-testid="slot-month-prev"
            aria-label="Mese precedente"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <button
            type="button"
            onClick={nextMonth}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 transition-colors"
            data-testid="slot-month-next"
            aria-label="Mese successivo"
          >
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
        {/* Calendar */}
        <div className="lg:col-span-3 p-5">
          {/* Weekday header */}
          <div
            className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.22em] text-slate-400 font-normal mb-2"
            data-testid="slot-calendar-weekdays"
          >
            <div>L</div>
            <div>M</div>
            <div>M</div>
            <div>G</div>
            <div>V</div>
            <div>S</div>
            <div>D</div>
          </div>

          {/* Grid days */}
          <div
            className="grid grid-cols-7 gap-1 text-center text-sm"
            data-testid="slot-calendar-grid"
          >
            {monthGrid.map((c, i) => {
              if (!c) return <div key={`e${i}`} className="h-10" />;
              const isSelected = selectedDate === c.date;
              const isToday = c.date === TODAY_ISO;
              const isPast = c.date < TODAY_ISO;
              const hasSlots = !!c.hasSlots;
              const disabled = isPast || !hasSlots;

              // Iter208.71 — classi Google Calendar style con celle rettangolari:
              //  - selezionato: fill viola (#8B5CF6/purple-600) + testo bianco
              //  - oggi (non selezionato): bordo viola sul rettangolo + testo viola
              //  - disponibile: hover leggero + pallino verde
              //  - passato/disabled: grigio chiaro, non cliccabile
              let cellCls =
                "relative w-10 h-10 mx-auto inline-flex items-center justify-center rounded-lg text-[13px] transition-all outline-none focus-visible:ring-2 focus-visible:ring-purple-300 ";
              if (isSelected) {
                cellCls += "bg-purple-600 text-white font-semibold shadow-sm ";
              } else if (isToday && !disabled) {
                cellCls += "border border-purple-500 text-purple-700 font-semibold hover:bg-purple-50 cursor-pointer ";
              } else if (isToday && disabled) {
                cellCls += "border border-slate-300 text-slate-400 cursor-not-allowed ";
              } else if (disabled) {
                cellCls += "text-slate-300 cursor-not-allowed ";
              } else {
                cellCls += "text-slate-700 font-normal hover:bg-slate-100 cursor-pointer ";
              }

              return (
                <button
                  key={c.date}
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && onSetSelectedDate(c.date)}
                  data-testid={`slot-day-${c.date}`}
                  data-has-slots={hasSlots ? "1" : "0"}
                  data-today={isToday ? "1" : "0"}
                  data-past={isPast ? "1" : "0"}
                  aria-pressed={isSelected}
                  aria-current={isToday ? "date" : undefined}
                  className={cellCls}
                >
                  {c.day}
                  {hasSlots && !isSelected && !isPast && (
                    <span
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-emerald-500"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div className="text-[11px] text-slate-500 mt-4 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-emerald-500" />
              Slot disponibili
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded border border-purple-500" />
              Oggi
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-purple-600" />
              Selezionato
            </span>
          </div>
        </div>

        {/* Slot list */}
        <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l border-slate-100 p-5 bg-slate-50/40">
          {!selectedDate ? (
            <div
              className="h-full flex items-center justify-center text-center text-xs text-slate-500 py-10"
              data-testid="slot-empty-day"
            >
              Seleziona una data
              <br />
              dal calendario
            </div>
          ) : (
            <>
              <div className="text-sm font-semibold text-slate-900 capitalize">
                {new Date(selectedDate).toLocaleDateString("it-IT", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </div>
              <div className="text-[11px] text-slate-500 mb-3">
                {slotsForSelectedDate.length > 0
                  ? `${slotsForSelectedDate.length} orari disponibili`
                  : "Nessun orario disponibile"}
              </div>

              {/* Iter208.66 — Chip in griglia, non lista */}
              <div
                className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1"
                data-testid="slot-hours-list"
              >
                {slotsForSelectedDate.map((s) => {
                  const sm = Number(s.minute || 0);
                  const active =
                    chosenSlot?.date === s.date &&
                    chosenSlot?.hour === s.hour &&
                    Number(chosenSlot?.minute || 0) === sm;
                  const hh = String(s.hour).padStart(2, "0");
                  const mm = String(sm).padStart(2, "0");
                  return (
                    <button
                      key={`${s.date}_${s.hour}_${sm}`}
                      type="button"
                      onClick={() => onSetChosenSlot(s)}
                      className={
                        "w-full h-10 inline-flex items-center justify-center rounded-lg text-[13px] font-medium transition-all border " +
                        (active
                          ? "bg-purple-600 text-white border-purple-600 shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-purple-400 hover:text-purple-700")
                      }
                      data-testid={`slot-hour-${s.date}-${s.hour}-${sm}`}
                    >
                      {hh}:{mm}
                    </button>
                  );
                })}
              </div>

              {chosenSlot && chosenSlot.date === selectedDate && (
                <div
                  className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs"
                  data-testid="slot-chosen-info"
                >
                  <div className="text-emerald-900 font-semibold">Slot confermato</div>
                  <div className="text-emerald-800/80 mt-0.5">
                    Un nostro tecnico ti raggiungerà per il sopralluogo nella data e ora scelte.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
